import { API_CONFIG } from '../config/api';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const REFRESH_ENDPOINT_PATTERN = /\/api\/v1\/auth\/refresh\/?$/i;

export const buildApiUrl = (endpoint = '') => {
  if (!endpoint) {
    return API_CONFIG.BASE_URL;
  }

  try {
    return new URL(endpoint).toString();
  } catch {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${API_CONFIG.BASE_URL}${normalizedEndpoint}`;
  }
};

export const getCookieValue = (name) => {
  if (typeof document === 'undefined' || !name) {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const prefix = `${encodeURIComponent(name)}=`;

  for (const cookie of cookies) {
    if (!cookie.startsWith(prefix)) {
      continue;
    }

    const rawValue = cookie.slice(prefix.length);
    try {
      return decodeURIComponent(rawValue);
    } catch {
      return rawValue;
    }
  }

  return null;
};

export const isMutatingMethod = (method = 'GET') =>
  MUTATING_METHODS.has(method.toUpperCase());

export const resolveCsrfCookieName = (url, method = 'GET') => {
  if (!isMutatingMethod(method)) {
    return null;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : API_CONFIG.BASE_URL;
  const pathname = new URL(url, origin).pathname;
  return REFRESH_ENDPOINT_PATTERN.test(pathname)
    ? 'csrf_refresh_token'
    : 'csrf_access_token';
};

export const buildRequestHeaders = ({
  url,
  method = 'GET',
  headers = {},
  isFormData = false
}) => {
  const requestHeaders = {
    ...(isFormData ? {} : API_CONFIG.HEADERS),
    ...headers
  };

  if (isFormData) {
    delete requestHeaders['Content-Type'];
  }

  const hasCsrfHeader = requestHeaders['X-CSRF-TOKEN'] || requestHeaders['x-csrf-token'];
  const csrfCookieName = resolveCsrfCookieName(url, method);

  if (!hasCsrfHeader && csrfCookieName) {
    const csrfToken = getCookieValue(csrfCookieName);
    if (csrfToken) {
      requestHeaders['X-CSRF-TOKEN'] = csrfToken;
    }
  }

  return requestHeaders;
};

export const apiFetch = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    headers = {},
    data,
    body,
    isFormData = false,
    credentials = 'include',
    ...rest
  } = options;

  const url = buildApiUrl(endpoint);
  const requestHeaders = buildRequestHeaders({ url, method, headers, isFormData });

  const requestOptions = {
    ...rest,
    method,
    headers: requestHeaders,
    credentials
  };

  if (body !== undefined) {
    requestOptions.body = body;
  } else if (data !== null && data !== undefined) {
    requestOptions.body = isFormData ? data : JSON.stringify(data);
  }

  return fetch(url, requestOptions);
};

export const parseResponseBody = async (response) => {
  if (!response || response.status === 204) {
    return null;
  }

  const rawText = await response.text();
  if (!rawText) {
    return null;
  }

  try {
    return JSON.parse(rawText);
  } catch {
    return rawText;
  }
};

export const buildResponseError = (response, responseBody, fallbackMessage) => {
  const message =
    (responseBody && typeof responseBody === 'object' && responseBody.message)
      || fallbackMessage
      || `Request failed: ${response?.status ?? 'unknown'}`;

  const error = new Error(message);
  error.status = response?.status;

  if (responseBody && typeof responseBody === 'object') {
    error.errorData = responseBody;
    error.errors = responseBody.errors;
    error.details = responseBody.details;
  }

  return error;
};
